import React, { use, useEffect, useState } from "react";
import { Tabs, Tab, Row, Col, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FaFolderOpen, FaTasks } from "react-icons/fa";
import { TbPackages } from "react-icons/tb";
import { toast } from "react-toastify";
import { unparse as parse } from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Projects from "./projects/Projects";
import WorkPackages from "./workPackages/WorkPackages";
import Tasks from "./tasks/Tasks";
import Deliverables from "./deliverables/Deliverables";
import PageTitle from "../../components/pageTitle";
import NoDataFound from "../../components/NoDataFound";
import { fetchProjects } from "../../store/features/project/projectSlice";
import { fetchWorkPackages } from "../../store/features/workPackage/workPackageSlice";
import { fetchTasks } from "../../store/features/task/taskSlice";
import { fetchDeliverables } from "../../store/features/deliverable/deliverableSlice";

import {
  generateHierarchicalWord,
  generateProjectsWord,
  generateWorkPackagesWord,
  generateDeliverablesWord,
  generateTasksWord,
} from "../../utils/exportWord";

import {
  fetchAssistantSuperadmin,
  fetchManagers,
} from "../../store/features/user/userSlice";
import { resetProjectById } from "../../store/features/project/projectSlice";

const ProjectsPackagesTaskWrapper = () => {
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const { paginationObj, projects } = useSelector((state) => state.projects);

  const { workPackages } = useSelector((state) => state.workPackages);
  const { tasks } = useSelector((state) => state.tasks);
  const { deliverables } = useSelector((state) => state.deliverables);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabKey, setTabKey] = useState(() => {
    if (["superadmin", "assistant superadmin", "auditor"].includes(role))
      return "projects";
    if (role === "manager") return "workPackages";
    return "tasks";
  });
  const [modals, setModals] = useState({
    showProjectModal: false,
    showTaskModal: false,
    showWpModal: false,
    showDeliverableModal: false,
  });

  const [info, setInfo] = useState({
    projectInfo: {},
    taskInfo: {},
    workPackagesInfo: {},
    deliverablesInfo: {},
    wpInfo: {},
    editingProject: null,
  });
  const [downloading, setDownloading] = useState({
    projects: false,
    workPackages: false,
    deliverables: false,
    tasks: false,
  });

  const isProjectShow = [
    "superadmin",
    "assistant superadmin",
    "auditor",
  ].includes(role);
  const isWpShow = [
    "superadmin",
    "assistant superadmin",
    "auditor",
    "manager",
  ].includes(role);

  useEffect(() => {
    dispatch(fetchProjects({ page: 1, limit: 10 }));
    dispatch(fetchWorkPackages());
    dispatch(fetchTasks());
    dispatch(fetchDeliverables());
    dispatch(fetchManagers());
    dispatch(fetchAssistantSuperadmin());
  }, [dispatch]);

  useEffect(() => {
    if (tabKey === "workPackages") {
      dispatch(resetProjectById());
    }
  }, [tabKey]);

  const getModalSetter = () => {
    switch (tabKey) {
      case "projects":
        return (val) =>
          setModals((prev) => ({ ...prev, showProjectModal: val }));
      case "workPackages":
        return (val) => setModals((prev) => ({ ...prev, showWpModal: val }));
      case "deliverables":
        return (val) =>
          setModals((prev) => ({ ...prev, showDeliverableModal: val }));
      default:
        return (val) => setModals((prev) => ({ ...prev, showTaskModal: val }));
    }
  };

  const getEditingSetter = () => {
    switch (tabKey) {
      case "projects":
        return (data) => setInfo((prev) => ({ ...prev, editingProject: data }));
      case "workPackages":
        return (data) =>
          setInfo((prev) => ({ ...prev, workPackagesInfo: data }));
      case "deliverables":
        return (data) =>
          setInfo((prev) => ({ ...prev, deliverablesInfo: data }));
      default:
        return (data) => setInfo((prev) => ({ ...prev, taskInfo: data }));
    }
  };

  const pageTitleMap = {
    projects: "Projects",
    workPackages: "Work Package",
    deliverables: "Deliverables",
    tasks: "Task",
  };

  const actionNameMap = {
    projects: "Add Project",
    workPackages: "Add Work Package",
    deliverables: "Add Deliverables",
    tasks: "Add Task",
  };

  const pageTitle = pageTitleMap[tabKey] || "Task";
  let actionName = actionNameMap[tabKey] || "Add Task";

  if (tabKey === "workPackages" && role === "manager") {
    actionName = "";
  }
  if (
    (tabKey === "deliverables" && role === "staff") ||
    (tabKey === "tasks" && role === "staff")
  ) {
    actionName = "";
  }

  const handleExportAllData = async () => {
    try {
      setDownloading((p) => ({ ...p, allData: true }));
      const rows = [];
      const data = projects || [];
      let lastProjectSNo = null;

      data.forEach((project, projectIndex) => {
        const projectSNo = projectIndex + 1;
        const relatedWPs = (workPackages || []).filter(
          (wp) => wp.projectId === project._id
        );
        let projectEmitted = false;

        if (relatedWPs.length === 0) {
          rows.push({
            SNo: projectSNo !== lastProjectSNo ? projectSNo : "",
            ProjectName: project.projectName,
            ProjectDescription: project.description,
            ProjectStartDate: project.startDate
              ? new Date(project.startDate).toLocaleDateString("en-US")
              : "N/A",
            ProjectEndDate: project.endDate
              ? new Date(project.endDate).toLocaleDateString("en-US")
              : "N/A",
            ProjectBudget: project.budget,
            ProjectCost: project.cost,
            ProjectAssistantSuperAdmin:
              project.assistantSuperadmin?.name || "N/A",
            ProjectCreatedBy: project?.creator?.name || "N/A",
            ProjectDocuments: project.document?.length
              ? project.document.join(", ")
              : "No Documents",
            ProjectProgressLevel: project.progressLevel,
            WP_Title: "",
            Deliverable_Title: "",
            Task_Title: "",
          });
          projectEmitted = true;
        } else {
          relatedWPs.forEach((wp) => {
            const relatedDeliverables = (deliverables || []).filter(
              (dlv) => dlv.wpId === wp._id
            );
            let wpEmitted = false;

            if (relatedDeliverables.length === 0) {
              rows.push({
                SNo: projectSNo !== lastProjectSNo ? projectSNo : "",
                ProjectName: projectEmitted ? "" : project.projectName,
                ProjectDescription: projectEmitted ? "" : project.description,
                ProjectStartDate: projectEmitted
                  ? ""
                  : project.startDate
                  ? new Date(project.startDate).toLocaleDateString("en-US")
                  : "N/A",
                ProjectEndDate: projectEmitted
                  ? ""
                  : project.endDate
                  ? new Date(project.endDate).toLocaleDateString("en-US")
                  : "N/A",
                ProjectBudget: projectEmitted ? "" : project.budget,
                ProjectCost: projectEmitted ? "" : project.cost,
                ProjectAssistantSuperAdmin: projectEmitted
                  ? ""
                  : project.assistantSuperadmin?.name || "N/A",
                ProjectCreatedBy: projectEmitted
                  ? ""
                  : project?.creator?.name || "N/A",
                ProjectDocuments: projectEmitted
                  ? ""
                  : project.document?.length
                  ? project.document.join(", ")
                  : "No Documents",
                ProjectProgressLevel: projectEmitted
                  ? ""
                  : project.progressLevel,
                WP_Title: wp.title,
                WP_Description: wp.description,
                WP_Number: wp.wpNumber,
                WP_Status: wp.status,
                WP_Cost: wp.cost,
                WP_ApprovalStatus: wp.approvalStatus,
                WP_RejectionReason: wp.rejectionReason || "N/A",
                WP_ProgressLevel: wp.progressLevel ?? 0,
                WP_ProjectName: wp.project?.projectName || "N/A",
                WP_CreatedBy: wp.createdByUser?.name || "N/A",
                WP_ManagerNames: wp.managers?.length
                  ? wp.managers.map((m) => m.name).join(", ")
                  : "No Managers",
                WP_StartDate: wp.startDate
                  ? new Date(wp.startDate).toLocaleDateString("en-US")
                  : "N/A",
                WP_EndDate: wp.endDate
                  ? new Date(wp.endDate).toLocaleDateString("en-US")
                  : "N/A",
                WP_Attachments: wp.wpAttachments?.length
                  ? wp.wpAttachments.join(", ")
                  : "No Attachments",
                Deliverable_Title: "",
                Task_Title: "",
              });
              projectEmitted = true;
              wpEmitted = true;
            } else {
              relatedDeliverables.forEach((dlv) => {
                const relatedTasks = (tasks || []).filter(
                  (t) =>
                    (t.dlvId && t.dlvId === dlv._id) ||
                    (t.wpId && t.wpId === wp._id && !t.dlvId)
                );
                let dlvEmitted = false;

                if (relatedTasks.length === 0) {
                  rows.push({
                    SNo: projectSNo !== lastProjectSNo ? projectSNo : "",
                    ProjectName: projectEmitted ? "" : project.projectName,
                    ProjectDescription: projectEmitted
                      ? ""
                      : project.description,
                    ProjectStartDate: projectEmitted
                      ? ""
                      : project.startDate
                      ? new Date(project.startDate).toLocaleDateString("en-US")
                      : "N/A",
                    ProjectEndDate: projectEmitted
                      ? ""
                      : project.endDate
                      ? new Date(project.endDate).toLocaleDateString("en-US")
                      : "N/A",
                    ProjectBudget: projectEmitted ? "" : project.budget,
                    ProjectCost: projectEmitted ? "" : project.cost,
                    ProjectAssistantSuperAdmin: projectEmitted
                      ? ""
                      : project.assistantSuperadmin?.name || "N/A",
                    ProjectCreatedBy: projectEmitted
                      ? ""
                      : project?.creator?.name || "N/A",
                    ProjectDocuments: projectEmitted
                      ? ""
                      : project.document?.length
                      ? project.document.join(", ")
                      : "No Documents",
                    ProjectProgressLevel: projectEmitted
                      ? ""
                      : project.progressLevel,
                    WP_Title: wpEmitted ? "" : wp.title,
                    WP_Description: wpEmitted ? "" : wp.description,
                    WP_Number: wpEmitted ? "" : wp.wpNumber,
                    WP_Status: wpEmitted ? "" : wp.status,
                    WP_Cost: wpEmitted ? "" : wp.cost,
                    WP_ApprovalStatus: wpEmitted ? "" : wp.approvalStatus,
                    WP_RejectionReason: wpEmitted
                      ? ""
                      : wp.rejectionReason || "N/A",
                    WP_ProgressLevel: wpEmitted ? "" : wp.progressLevel ?? 0,
                    WP_ProjectName: wpEmitted
                      ? ""
                      : wp.project?.projectName || "N/A",
                    WP_CreatedBy: wpEmitted
                      ? ""
                      : wp.createdByUser?.name || "N/A",
                    WP_ManagerNames: wpEmitted
                      ? ""
                      : wp.managers?.length
                      ? wp.managers.map((m) => m.name).join(", ")
                      : "No Managers",
                    WP_StartDate: wpEmitted
                      ? ""
                      : wp.startDate
                      ? new Date(wp.startDate).toLocaleDateString("en-US")
                      : "N/A",
                    WP_EndDate: wpEmitted
                      ? ""
                      : wp.endDate
                      ? new Date(wp.endDate).toLocaleDateString("en-US")
                      : "N/A",
                    WP_Attachments: wpEmitted
                      ? ""
                      : wp.wpAttachments?.length
                      ? wp.wpAttachments.join(", ")
                      : "No Attachments",
                    Deliverable_Title: dlv.title,
                    Deliverable_Description: dlv.description || "N/A",
                    Deliverable_Number: dlv.dlvNumber,
                    Deliverable_Cost: dlv.cost ?? 0,
                    Deliverable_Status: dlv.status || "N/A",
                    Deliverable_ProgressLevel: dlv.progressLevel ?? 0,
                    Deliverable_ProjectName: dlv.project?.projectName || "N/A",
                    Deliverable_WPTitle: dlv.workPackage?.title || "N/A",
                    Deliverable_AssignedTo: dlv.assignedUser?.name || "N/A",
                    Deliverable_CreatedBy: dlv.createdByUser?.name || "N/A",
                    Deliverable_StartDate: dlv.startDate
                      ? new Date(dlv.startDate).toLocaleDateString("en-US")
                      : "N/A",
                    Deliverable_EndDate: dlv.endDate
                      ? new Date(dlv.endDate).toLocaleDateString("en-US")
                      : "N/A",
                    Deliverable_Files: dlv.deliverablesFiles?.length
                      ? dlv.deliverablesFiles.join(", ")
                      : "No Files",
                    Task_Title: "",
                  });
                  projectEmitted = true;
                  wpEmitted = true;
                  dlvEmitted = true;
                } else {
                  relatedTasks.forEach((task) => {
                    rows.push({
                      SNo: projectSNo !== lastProjectSNo ? projectSNo : "",
                      ProjectName: projectEmitted ? "" : project.projectName,
                      ProjectDescription: projectEmitted
                        ? ""
                        : project.description,
                      ProjectStartDate: projectEmitted
                        ? ""
                        : project.startDate
                        ? new Date(project.startDate).toLocaleDateString(
                            "en-US"
                          )
                        : "N/A",
                      ProjectEndDate: projectEmitted
                        ? ""
                        : project.endDate
                        ? new Date(project.endDate).toLocaleDateString("en-US")
                        : "N/A",
                      ProjectBudget: projectEmitted ? "" : project.budget,
                      ProjectCost: projectEmitted ? "" : project.cost,
                      ProjectAssistantSuperAdmin: projectEmitted
                        ? ""
                        : project.assistantSuperadmin?.name || "N/A",
                      ProjectCreatedBy: projectEmitted
                        ? ""
                        : project?.creator?.name || "N/A",
                      ProjectDocuments: projectEmitted
                        ? ""
                        : project.document?.length
                        ? project.document.join(", ")
                        : "No Documents",
                      ProjectProgressLevel: projectEmitted
                        ? ""
                        : project.progressLevel,
                      WP_Title: wpEmitted ? "" : wp.title,
                      WP_Description: wpEmitted ? "" : wp.description,
                      WP_Number: wpEmitted ? "" : wp.wpNumber,
                      WP_Status: wpEmitted ? "" : wp.status,
                      WP_Cost: wpEmitted ? "" : wp.cost,
                      WP_ApprovalStatus: wpEmitted ? "" : wp.approvalStatus,
                      WP_RejectionReason: wpEmitted
                        ? ""
                        : wp.rejectionReason || "N/A",
                      WP_ProgressLevel: wpEmitted ? "" : wp.progressLevel ?? 0,
                      WP_ProjectName: wpEmitted
                        ? ""
                        : wp.project?.projectName || "N/A",
                      WP_CreatedBy: wpEmitted
                        ? ""
                        : wp.createdByUser?.name || "N/A",
                      WP_ManagerNames: wpEmitted
                        ? ""
                        : wp.managers?.length
                        ? wp.managers.map((m) => m.name).join(", ")
                        : "No Managers",
                      WP_StartDate: wpEmitted
                        ? ""
                        : wp.startDate
                        ? new Date(wp.startDate).toLocaleDateString("en-US")
                        : "N/A",
                      WP_EndDate: wpEmitted
                        ? ""
                        : wp.endDate
                        ? new Date(wp.endDate).toLocaleDateString("en-US")
                        : "N/A",
                      WP_Attachments: wpEmitted
                        ? ""
                        : wp.wpAttachments?.length
                        ? wp.wpAttachments.join(", ")
                        : "No Attachments",
                      Deliverable_Title: dlvEmitted ? "" : dlv.title,
                      Deliverable_Description: dlvEmitted
                        ? ""
                        : dlv.description || "N/A",
                      Deliverable_Number: dlvEmitted ? "" : dlv.dlvNumber,
                      Deliverable_Cost: dlvEmitted ? "" : dlv.cost ?? 0,
                      Deliverable_Status: dlvEmitted ? "" : dlv.status || "N/A",
                      Deliverable_ProgressLevel: dlvEmitted
                        ? ""
                        : dlv.progressLevel ?? 0,
                      Deliverable_ProjectName: dlvEmitted
                        ? ""
                        : dlv.project?.projectName || "N/A",
                      Deliverable_WPTitle: dlvEmitted
                        ? ""
                        : dlv.workPackage?.title || "N/A",
                      Deliverable_AssignedTo: dlvEmitted
                        ? ""
                        : dlv.assignedUser?.name || "N/A",
                      Deliverable_CreatedBy: dlvEmitted
                        ? ""
                        : dlv.createdByUser?.name || "N/A",
                      Deliverable_StartDate: dlvEmitted
                        ? ""
                        : dlv.startDate
                        ? new Date(dlv.startDate).toLocaleDateString("en-US")
                        : "N/A",
                      Deliverable_EndDate: dlvEmitted
                        ? ""
                        : dlv.endDate
                        ? new Date(dlv.endDate).toLocaleDateString("en-US")
                        : "N/A",
                      Deliverable_Files: dlvEmitted
                        ? ""
                        : dlv.deliverablesFiles?.length
                        ? dlv.deliverablesFiles.join(", ")
                        : "No Files",
                      Task_Title: task.title,
                      Task_Description: task.description || "N/A",
                      Task_CompletionNotes: task.completionNotes || "N/A",
                      Task_ApprovalStatus: task.approvalStatus || "N/A",
                      Task_RejectionReason: task.rejectionReason || "N/A",
                      Task_ProgressLevel: task.progressLevel ?? 0,
                      Task_ProjectName: task.project?.projectName || "N/A",
                      Task_WPTitle: task.workPackage?.title || "N/A",
                      Task_AssignedTo: task.assignedUser?.name || "N/A",
                      Task_AssignedBy: task.assignedByUser?.name || "N/A",
                      Task_CreatedBy: task.createdByUser?.name || "N/A",
                      Task_StartDate: task.startDate
                        ? new Date(task.startDate).toLocaleDateString("en-US")
                        : "N/A",
                      Task_EndDate: task.endDate
                        ? new Date(task.endDate).toLocaleDateString("en-US")
                        : "N/A",
                      Task_Attachments: task.attachments?.length
                        ? task.attachments.join(", ")
                        : "No Attachments",
                    });
                    projectEmitted = true;
                    wpEmitted = true;
                    dlvEmitted = true;
                  });
                }
              });
            }
          });
        }
      });
      generateHierarchicalWord(rows);
      toast.success("All data exported successfully!");
    } catch (error) {
      toast.error("Failed to export all data!");
    } finally {
      setDownloading((p) => ({ ...p, allData: false }));
    }
  };

  const handleExportProjects = async () => {
    try {
      setDownloading((p) => ({ ...p, projects: true }));

      const formattedData = projects.map((project, index) => ({
        SNo: index + 1,
        ProjectName: project.projectName,
        Description: project.description,
        StartDate: new Date(project.startDate).toLocaleDateString("en-US"),
        EndDate: new Date(project.endDate).toLocaleDateString("en-US"),
        Budget: project.budget,
        Cost: project.cost,
        AssistantSuperAdmin: project.assistantSuperadmin?.name || "N/A",
        CreatedBy: project?.creator?.name || "N/A",
        Documents: project.document?.length
          ? project.document.join(", ")
          : "No Documents",
        TaskCount: project.taskCount,
        WorkPackageCount: project.workpackageCount,
        ProgressLevel: project.progressLevel,
      }));

      await generateProjectsWord(formattedData);
      toast.success("Projects exported!");
    } catch (err) {
      toast.error("Failed exporting projects.");
    } finally {
      setDownloading((p) => ({ ...p, projects: false }));
    }
  };

  const handleExportWorkPackages = async () => {
    try {
      setDownloading((p) => ({ ...p, workPackages: true }));

      const formattedData = workPackages.map((wp, index) => ({
        SNo: index + 1,
        Title: wp.title,
        Description: wp.description,
        WPNumber: wp.wpNumber,
        Status: wp.status,
        Cost: wp.cost,
        ApprovalStatus: wp.approvalStatus,
        RejectionReason: wp.rejectionReason || "N/A",
        ProgressLevel: wp.progressLevel ?? 0,
        ProjectName: wp.project?.projectName || "N/A",
        CreatedBy: wp.createdByUser?.name || "N/A",
        ManagerNames: wp.managers?.length
          ? wp.managers.map((m) => m.name).join(", ")
          : "No Managers",
        StartDate: wp.startDate
          ? new Date(wp.startDate).toLocaleDateString("en-US")
          : "N/A",
        EndDate: wp.endDate
          ? new Date(wp.endDate).toLocaleDateString("en-US")
          : "N/A",
        Attachments: wp.wpAttachments?.length
          ? wp.wpAttachments.join(", ")
          : "No Attachments",
        TaskCount: wp.taskCount ?? 0,
        DeliverableCount: wp.deliverableCount ?? 0,
      }));

      await generateWorkPackagesWord(formattedData);
      toast.success("Work packages exported!");
    } catch (err) {
      toast.error("Failed exporting work packages.");
    } finally {
      setDownloading((p) => ({ ...p, workPackages: false }));
    }
  };

  const handleExportDeliverables = async () => {
    try {
      setDownloading((p) => ({ ...p, deliverables: true }));

      const formattedData = deliverables.map((dlv, index) => ({
        SNo: index + 1,
        Title: dlv.title,
        DlvNumber: dlv.dlvNumber,
        Description: dlv.description || "N/A",
        Cost: dlv.cost ?? 0,
        Status: dlv.status || "N/A",
        ProgressLevel: dlv.progressLevel ?? 0,
        ProjectName: dlv.project?.projectName || "N/A",
        WorkPackageTitle: dlv.workPackage?.title || "N/A",
        AssignedTo: dlv.assignedUser?.name || "N/A",
        CreatedBy: dlv.createdByUser?.name || "N/A",
        StartDate: dlv.startDate
          ? new Date(dlv.startDate).toLocaleDateString("en-US")
          : "N/A",
        EndDate: dlv.endDate
          ? new Date(dlv.endDate).toLocaleDateString("en-US")
          : "N/A",
        DeliverableFiles: dlv.deliverablesFiles?.length
          ? dlv.deliverablesFiles.join(", ")
          : "No Files",
        TaskCount: dlv.taskCount ?? 0,
      }));

      await generateDeliverablesWord(formattedData);
      toast.success("Deliverables exported!");
    } catch (err) {
      toast.error("Failed exporting deliverables.");
    } finally {
      setDownloading((p) => ({ ...p, deliverables: false }));
    }
  };
  const handleExportTasks = async () => {
    try {
      setDownloading((p) => ({ ...p, tasks: true }));

      const formattedData = tasks.map((task, index) => ({
        SNo: index + 1,
        Title: task.title,
        Description: task.description || "N/A",
        CompletionNotes: task.completionNotes || "N/A",
        ApprovalStatus: task.approvalStatus || "N/A",
        RejectionReason: task.rejectionReason || "N/A",
        ProgressLevel: task.progressLevel ?? 0,
        ProjectName: task.project?.projectName || "N/A",
        WorkPackageTitle: task.workPackage?.title || "N/A",
        AssignedTo: task.assignedUser?.name || "N/A",
        AssignedBy: task.assignedByUser?.name || "N/A",
        CreatedBy: task.createdByUser?.name || "N/A",
        StartDate: task.startDate
          ? new Date(task.startDate).toLocaleDateString("en-US")
          : "N/A",
        EndDate: task.endDate
          ? new Date(task.endDate).toLocaleDateString("en-US")
          : "N/A",
        Attachments: task.attachments?.length
          ? task.attachments.join(", ")
          : "No Attachments",
      }));

      await generateTasksWord(formattedData);
      toast.success("Tasks exported!");
    } catch (err) {
      toast.error("Failed exporting tasks.");
    } finally {
      setDownloading((p) => ({ ...p, tasks: false }));
    }
  };

  return (
    <>
      <PageTitle
        setShowForm={getModalSetter()}
        pageTitle={pageTitle}
        pageSubtitle="Manage and monitor all project activities"
        actionName={role !== "auditor" && actionName}
        setEditing={getEditingSetter()}
      />

      <Row className="mb-4">
        {isProjectShow && (
          <Col md={3}>
            <div className="card mb-2">
              <div className="card-item align-items-center p-2">
                <div className="icon me-2">
                  <FaFolderOpen />
                </div>
                <div className="card-title flex-grow-1">
                  <div className="item">Total Projects</div>
                  <div className="item-count">
                    {paginationObj?.totalCount || 0}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        )}

        {isWpShow && (
          <Col md={3}>
            <div className="card mb-2">
              <div className="card-item align-items-center p-2">
                <div className="icon me-2">
                  <TbPackages />
                </div>
                <div className="card-title flex-grow-1">
                  <div className="item">Total Work Packages</div>
                  <div className="item-count">
                    {role === "manager"
                      ? workPackages.filter((w) => !w.rejectionReason).length
                      : workPackages.length || 0}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        )}

        <Col md={3}>
          <div className="card mb-2">
            <div className="card-item align-items-center p-2">
              <div className="icon me-2">
                <FaTasks />
              </div>
              <div className="card-title flex-grow-1">
                <div className="item">Total Deliverbles</div>
                <div className="item-count">{deliverables.length || 0}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col md={3}>
          <div className="card mb-2">
            <div className="card-item align-items-center p-2">
              <div className="icon me-2">
                <FaTasks />
              </div>
              <div className="card-title flex-grow-1">
                <div className="item">Total Tasks</div>
                <div className="item-count">
                  {role === "staff"
                    ? tasks.filter((t) => !t.rejectionReason).length
                    : tasks.length || 0}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Tabs
        activeKey={tabKey}
        onSelect={setTabKey}
        id="project-tabs"
        className="mb-3"
        mountOnEnter
        unmountOnExit
      >
        {isProjectShow && (
          <Tab eventKey="projects" title="Projects">
            {/* <button>Export Projects</button> */}
            <Row>
              {/* <Col md="6">
                <Form.Control
                  type="text"
                  name="userSearch"
                  value={searchTerm}
                  placeholder="Search by name..."
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col> */}

              <div class="d-flex justify-content-end">
                <button
                  className="btn btn-secondary border-btn"
                  onClick={handleExportAllData}
                >
                  Export All Details
                </button>{" "}
                <button
                  onClick={handleExportProjects}
                  disabled={downloading.projects}
                  className="btn btn-primary"
                >
                  {downloading.projects
                    ? "Downloading Projects..."
                    : "Export Projects"}
                </button>
              </div>
            </Row>

            <Projects
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              setShowForm={(val) =>
                setModals((p) => ({ ...p, showProjectModal: val }))
              }
              showForm={modals.showProjectModal}
              setProjectInfo={(data) =>
                setInfo((p) => ({ ...p, projectInfo: data }))
              }
              editingProject={info.editingProject}
              setEditingProject={(data) =>
                setInfo((p) => ({ ...p, editingProject: data }))
              }
              setTabKey={setTabKey}
              setWpInfo={(data) => setInfo((p) => ({ ...p, wpInfo: data }))}
              role={role}
            />
          </Tab>
        )}

        {isWpShow && (
          <Tab eventKey="workPackages" title="Work Packages">
            {/* {workPackages.length > 0 ? ( */}
            <div class="d-flex justify-content-end">
              <button
                onClick={handleExportWorkPackages}
                disabled={downloading.workPackages}
                className="btn btn-primary"
              >
                {downloading.workPackages
                  ? "Downloading Work Packages..."
                  : "Export Work Packages"}
              </button>
            </div>

            <WorkPackages
              projectInfo={
                info.projectInfo?._id
                  ? info.projectInfo
                  : info.workPackagesInfo.project
              }
              workPackagesInfo={info.workPackagesInfo}
              setProjectInfo={(data) =>
                setInfo((p) => ({ ...p, projectInfo: data }))
              }
              setWorkPackaesInfo={(data) =>
                setInfo((p) => ({ ...p, workPackagesInfo: data }))
              }
              setShowWpModal={(val) =>
                setModals((p) => ({ ...p, showWpModal: val }))
              }
              showWpModal={modals.showWpModal}
              setTabKey={setTabKey}
              setWpInfo={(data) => setInfo((p) => ({ ...p, wpInfo: data }))}
              role={role}
            />
          </Tab>
        )}

        <Tab eventKey="deliverables" title="Deliverables">
          <div class="d-flex justify-content-end">
            <button
              onClick={handleExportDeliverables}
              disabled={downloading.deliverables}
              className="btn btn-primary"
            >
              {downloading.deliverables
                ? "Downloading Deliverables..."
                : "Export Deliverables"}
            </button>
          </div>

          {/* {deliverables.length > 0 ? ( */}
          <Deliverables
            deliverablesInfo={info.deliverablesInfo}
            setDeliverablesInfo={(data) =>
              setInfo((p) => ({ ...p, deliverablesInfo: data }))
            }
            setShowDeliverableModal={(val) =>
              setModals((p) => ({ ...p, showDeliverableModal: val }))
            }
            showDeliverableModal={modals.showDeliverableModal}
            setTabKey={setTabKey}
            wpInfo={info.wpInfo?._id ? info.wpInfo : info.taskInfo.workPackage}
            setWpInfo={(data) => setInfo((p) => ({ ...p, wpInfo: data }))}
          />
        </Tab>

        <Tab eventKey="tasks" title="Tasks">
          <div className="d-flex justify-content-end">
            <button
              onClick={handleExportTasks}
              disabled={downloading.tasks}
              className="btn btn-primary"
            >
              {downloading.tasks ? "Downloading Tasks..." : "Export Tasks"}
            </button>
          </div>
          {/* {tasks.length > 0 ? ( */}
          <Tasks
            wpInfo={info.wpInfo?._id ? info.wpInfo : info.taskInfo.workPackage}
            setWpInfo={(data) => setInfo((p) => ({ ...p, wpInfo: data }))}
            taskInfo={info.taskInfo}
            setTaskInfo={(data) => setInfo((p) => ({ ...p, taskInfo: data }))}
            setShowTaskModal={(val) =>
              setModals((p) => ({ ...p, showTaskModal: val }))
            }
            showTaskModal={modals.showTaskModal}
            deliverablesInfo={info.deliverablesInfo}
            setDeliverablesInfo={(data) =>
              setInfo((p) => ({ ...p, deliverablesInfo: data }))
            }
            role={role}
          />
        </Tab>
      </Tabs>
    </>
  );
};

export default ProjectsPackagesTaskWrapper;
